import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Dealers master — list + permission-gated create/edit/delete.
 * Each action gates purely on the effective permission set returned by
 * `/auth/me` (dealers.add / dealers.edit / dealers.delete), so admin can
 * grant these permissions to any role and the UI updates on next login.
 */
export default function DealersReadOnly() {
  const { can } = useAuth();
  const canAdd = can("dealers.add");
  const canEdit = can("dealers.edit");
  const canDelete = can("dealers.delete");
  const showActions = canEdit || canDelete;
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/masters/dealers").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const filtered = rows.filter((d) => !search || (d.name || "").toLowerCase().includes(search.toLowerCase()));

  const onDelete = async (dealer) => {
    if (!window.confirm(`Delete dealer "${dealer.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/masters/dealers/${dealer.id}`);
      toast.success(`Deleted ${dealer.name}`);
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <div className="space-y-5" data-testid="dealers-readonly-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dealers</h1>
          <p className="text-sm text-slate-500 mt-1">
            {rows.length} dealers in master{canAdd || canEdit || canDelete ? "" : " · read-only view"}.
          </p>
        </div>
        {canAdd && (
          <Button onClick={() => setShowCreate(true)} data-testid="dealers-create-btn">
            <Plus size={14} className="mr-1" /> New Dealer
          </Button>
        )}
      </div>
      <Card><CardContent className="p-4">
        <Input placeholder="Search dealer name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="dealers-search" />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b"><tr>
              <Th>Dealer Name</Th><Th>Contact</Th><Th>Phone</Th><Th>Address</Th><Th>Area</Th><Th>District</Th><Th>Province</Th>
              {showActions && <Th align="right">Actions</Th>}
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={showActions ? 8 : 7} className="py-12 text-center text-slate-400">No dealers found.</td></tr>}
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`dealer-${(d.name || "").replace(/\s+/g, "_")}`}>
                  <td className="font-medium">{d.name}</td>
                  <td>{d.contact_person || "—"}</td>
                  <td className="font-mono text-xs">{d.phone || "—"}</td>
                  <td className="text-xs text-slate-700 max-w-xs truncate" title={d.address || ""}>{d.address || "—"}</td>
                  <td className="text-xs">{d.area || "—"}</td>
                  <td className="text-xs">{d.district || "—"}</td>
                  <td className="text-xs">{d.province || "—"}</td>
                  {showActions && (
                    <td className="text-right whitespace-nowrap">
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(d)} data-testid={`dealers-edit-${d.id}`}>
                          <Edit size={14} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => onDelete(d)} data-testid={`dealers-delete-${d.id}`}>
                          <Trash2 size={14} className="text-rose-600" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <DealerCreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <DealerEditDialog dealer={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function DealerCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", address: "",
    area: "", district: "", province: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name?.trim()) {
      toast.error("Dealer name is required");
      return;
    }
    try {
      const { data } = await api.post("/masters/dealers", form);
      toast.success(`Dealer created: ${data.name}`);
      onCreated && onCreated();
      onClose();
      setForm({ name: "", contact_person: "", phone: "", address: "", area: "", district: "", province: "" });
    } catch (e) {
      toast.error("Could not create dealer", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dealer-create-dialog">
        <DialogHeader>
          <DialogTitle>New Dealer</DialogTitle>
          <DialogDescription>
            Any user with `dealers.add` may create a dealer directly. The record is immediately active.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pair label="Dealer Name *" col2>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="dealer-create-name" />
          </Pair>
          <Pair label="Contact Person">
            <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="dealer-create-contact" />
          </Pair>
          <Pair label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="dealer-create-phone" placeholder="+977-…" />
          </Pair>
          <Pair label="Address" col2>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
          </Pair>
          <Pair label="Area">
            <Input value={form.area} onChange={(e) => set("area", e.target.value)} />
          </Pair>
          <Pair label="District">
            <Input value={form.district} onChange={(e) => set("district", e.target.value)} />
          </Pair>
          <Pair label="Province" col2>
            <Input value={form.province} onChange={(e) => set("province", e.target.value)} />
          </Pair>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="dealer-create-submit">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DealerEditDialog({ dealer, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(dealer ? { ...dealer } : null); }, [dealer]);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Dealer name is required"); return; }
    try {
      const payload = {
        name: form.name, contact_person: form.contact_person, phone: form.phone,
        address: form.address, area: form.area, district: form.district, province: form.province,
      };
      await api.put(`/masters/dealers/${dealer.id}`, payload);
      toast.success(`Updated ${form.name}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not update dealer", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={!!dealer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dealer-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Dealer</DialogTitle>
          <DialogDescription>Requires `dealers.edit` permission.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pair label="Dealer Name *" col2>
            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="dealer-edit-name" />
          </Pair>
          <Pair label="Contact Person">
            <Input value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} />
          </Pair>
          <Pair label="Phone">
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          </Pair>
          <Pair label="Address" col2>
            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
          </Pair>
          <Pair label="Area">
            <Input value={form.area || ""} onChange={(e) => set("area", e.target.value)} />
          </Pair>
          <Pair label="District">
            <Input value={form.district || ""} onChange={(e) => set("district", e.target.value)} />
          </Pair>
          <Pair label="Province" col2>
            <Input value={form.province || ""} onChange={(e) => set("province", e.target.value)} />
          </Pair>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="dealer-edit-submit">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Pair({ label, col2, children }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
